<template>
  <div class="sidebar__section" ph-autocapture="sidebar" data-highlight="menu">
    <div class="sidebar__panel-head">
      <span class="sidebar__panel-title">{{ $t('sidebar.homePanel') }}</span>
      <a
        ref="panelCreateLink"
        class="sidebar__panel-add"
        @click="$refs.panelCreateContext.toggle($refs.panelCreateLink)"
      >
        <i class="iconoir-plus"></i>
      </a>
      <CreateApplicationContext
        ref="panelCreateContext"
        :workspace="selectedWorkspace"
      ></CreateApplicationContext>
    </div>
    <!-- Origem Flow: itens do painel na ordem escolhida pelo usuário
         (arrastar e soltar); persistida no localStorage. -->
    <ul class="tree">
      <template v-for="itemKey in itemOrder">
        <li
          v-if="itemKey === 'inbox'"
          :key="itemKey + '-inbox'"
          v-sortable="menuSortable('inbox')"
          class="tree__item"
        >
          <div class="tree__action tree__action--has-counter">
            <a
              class="tree__link"
              @click="$refs.notificationPanel.toggle($event.currentTarget)"
            >
              <i class="tree__icon tree__icon--type iconoir-mail"></i>
              <span class="tree__link-text">{{ $t('sidebar.inbox') }}</span>
            </a>
            <BadgeCounter
              v-show="unreadNotificationCount"
              class="tree__counter"
              :count="unreadNotificationCount"
              :limit="10"
            >
            </BadgeCounter>
          </div>
        </li>

        <nuxt-link
          v-else-if="itemKey === 'comments'"
          v-slot="{ href, navigate, isExactActive }"
          :key="itemKey + '-comments'"
          custom
          :to="{
            name: 'workspace-assigned-comments',
            params: { workspaceId: selectedWorkspace.id },
          }"
        >
          <li
            v-sortable="menuSortable('comments')"
            class="tree__item"
            :class="{ active: isExactActive }"
          >
            <div class="tree__action sidebar__action">
              <a :href="href" class="tree__link" @click="navigate">
                <i class="tree__icon iconoir-chat-bubble"></i>
                <span class="tree__link-text">
                  <span class="sidebar__item-name">{{
                    $t('sidebar.assignedComments')
                  }}</span>
                </span>
              </a>
            </div>
          </li>
        </nuxt-link>

        <nuxt-link
          v-else-if="itemKey === 'meetings'"
          v-slot="{ href, navigate, isExactActive }"
          :key="itemKey + '-meetings'"
          custom
          :to="{
            name: 'workspace-meetings',
            params: { workspaceId: selectedWorkspace.id },
          }"
        >
          <li
            v-sortable="menuSortable('meetings')"
            class="tree__item"
            :class="{ active: isExactActive }"
          >
            <div class="tree__action sidebar__action">
              <a :href="href" class="tree__link" @click="navigate">
                <i class="tree__icon iconoir-community"></i>
                <span class="tree__link-text">
                  <span class="sidebar__item-name">{{
                    $t('sidebar.meetings')
                  }}</span>
                </span>
              </a>
            </div>
          </li>
        </nuxt-link>

        <nuxt-link
          v-else-if="itemKey === 'mytasks'"
          v-slot="{ href, navigate, isExactActive }"
          :key="itemKey + '-mytasks'"
          custom
          :to="{
            name: 'workspace',
            params: {
              workspaceId: selectedWorkspace.id,
            },
            query: { card: 'mywork' },
          }"
        >
          <li
            v-sortable="menuSortable('mytasks')"
            class="tree__item"
            :class="{
              active: isExactActive,
            }"
          >
            <div class="tree__action tree__action--has-counter">
              <a :href="href" class="tree__link" @click="navigate">
                <i class="tree__icon iconoir-user"></i>
                <span class="tree__link-text">
                  <span class="sidebar__item-name">{{
                    $t('sidebar.myTasks')
                  }}</span>
                </span>
              </a>
              <BadgeCounter
                v-show="pendingTaskCount"
                class="tree__counter"
                :count="pendingTaskCount"
                :limit="100"
              >
              </BadgeCounter>
            </div>
          </li>
        </nuxt-link>
        <nuxt-link
          v-else-if="itemKey === 'manual'"
          v-slot="{ href, navigate, isExactActive }"
          :key="itemKey + '-manual'"
          custom
          :to="{
            name: 'workspace-manual',
            params: { workspaceId: selectedWorkspace.id },
          }"
        >
          <li
            v-sortable="menuSortable('manual')"
            class="tree__item"
            :class="{ active: isExactActive }"
          >
            <div class="tree__action sidebar__action">
              <a :href="href" class="tree__link" @click="navigate">
                <i class="tree__icon iconoir-book-stack" style="color: #40c057;"></i>
                <span class="tree__link-text">
                  <span class="sidebar__item-name">Central de Conhecimento</span>
                </span>
              </a>
            </div>
          </li>
        </nuxt-link>
      </template>
    </ul>
    <!-- Fora do v-for: ref dentro de v-for viraria array no Vue 3. -->
    <NotificationPanel ref="notificationPanel" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import NotificationPanel from '@baserow/modules/core/components/NotificationPanel'
import BadgeCounter from '@baserow/modules/core/components/BadgeCounter'
import CreateApplicationContext from '@baserow/modules/core/components/application/CreateApplicationContext'
import HomeService from '@baserow/modules/core/services/home'
import { TEMPLATE_PREFIX } from '@baserow/modules/core/utils/applicationTemplates'
import {
  readSidebarOrder,
  writeSidebarOrder,
} from '@baserow/modules/core/utils/sidebarOrder'

const MENU_ITEM_KEYS = ['inbox', 'comments', 'meetings', 'mytasks', 'manual']

/**
 * Origem Flow: painel contextual "Início" no formato do ClickUp — enxuto:
 * Caixa de entrada, Comentários atribuídos, Reuniões e Minhas tarefas (com
 * badge de pendentes). Respostas, Skills, Membros, Convidar, Registro de
 * auditoria e Lixeira vivem no AppRail (coluna de ícones).
 */
export default {
  name: 'SidebarMenu',
  components: {
    NotificationPanel,
    BadgeCounter,
    CreateApplicationContext,
  },
  props: {
    selectedWorkspace: {
      type: Object,
      required: true,
    },
    rightSidebarOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['open-workspace-search'],
  data() {
    return {
      pendingTaskCount: 0,
      // Ordem dos itens do painel (arrastar e soltar para trocar).
      itemOrder: [...MENU_ITEM_KEYS],
    }
  },
  computed: {
    ...mapGetters({
      unreadNotificationCount: 'notification/getUnreadCount',
    }),
  },
  async mounted() {
    this.itemOrder = readSidebarOrder('home-panel', this.itemOrder)
    // Badge das "Minhas tarefas": linhas atribuídas ainda abertas.
    try {
      const { data } = await HomeService(this.$client).fetchAssignedRows(
        this.selectedWorkspace.id
      )
      this.pendingTaskCount = data.results.filter(
        (row) => !row.done && !row.database_name.startsWith(TEMPLATE_PREFIX)
      ).length
    } catch (error) {
      this.pendingTaskCount = 0
    }
  },
  methods: {
    menuSortable(key) {
      return { id: key, update: this.orderMenuItems }
    },
    orderMenuItems(newOrder) {
      const valid = newOrder.filter((key) => MENU_ITEM_KEYS.includes(key))
      if (valid.length !== MENU_ITEM_KEYS.length) {
        return
      }
      this.itemOrder = valid
      writeSidebarOrder('home-panel', valid)
    },
  },
}
</script>
